import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  CardHeader,
  Divider,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useTrip } from '../../contexts/TripContext';
import { useBooking } from '../../contexts/BookingContext';
import { useVehicle } from '../../contexts/VehicleContext';
import { useCompany } from '../../contexts/CompanyContext';

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const MainDashboard = () => {
  const { currentUser } = useAuth();
  const { getTrips } = useTrip();
  const { getBookings, getBookingStats } = useBooking();
  const { getVehicles } = useVehicle();
  const { getCompanies } = useCompany();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('week');
  
  // Dashboard data
  const [stats, setStats] = useState({
    totalTrips: 0,
    totalBookings: 0,
    totalVehicles: 0,
    totalCompanies: 0,
    totalRevenue: 0,
    occupancyRate: 0
  });
  
  // Chart data
  const [revenueData, setRevenueData] = useState([]);
  const [bookingStatusData, setBookingStatusData] = useState([]);
  const [tripsByDestinationData, setTripsByDestinationData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all required data
        const trips = await getTrips();
        const bookings = await getBookings();
        const vehicles = await getVehicles();
        const companies = await getCompanies();
        const bookingStats = await getBookingStats();
        
        // Calculate basic stats
        const totalTrips = trips.length;
        const totalBookings = bookings.length;
        const totalVehicles = vehicles.length;
        const totalCompanies = companies.length;
        const totalRevenue = bookingStats.totalRevenue || 0;
        
        // Calculate occupancy rate
        let totalSeats = 0;
        let bookedSeats = 0;
        
        trips.forEach(trip => {
          if (trip.status === 'completed' || trip.status === 'scheduled') {
            totalSeats += trip.totalSeats || 0;
            bookedSeats += (trip.totalSeats - trip.availableSeats) || 0;
          }
        });
        
        const occupancyRate = totalSeats > 0 ? (bookedSeats / totalSeats) * 100 : 0;
        
        setStats({
          totalTrips,
          totalBookings,
          totalVehicles,
          totalCompanies,
          totalRevenue,
          occupancyRate
        });
        
        // Prepare revenue data for chart
        const revenueByDate = prepareRevenueData(bookings, timeRange);
        setRevenueData(revenueByDate);
        
        // Prepare booking status data for chart
        const bookingsByStatus = [
          { name: 'Confirmed', value: bookings.filter(b => b.status === 'confirmed').length },
          { name: 'Pending', value: bookings.filter(b => b.status === 'pending').length },
          { name: 'Cancelled', value: bookings.filter(b => b.status === 'cancelled').length },
          { name: 'Completed', value: bookings.filter(b => b.status === 'completed').length }
        ];
        setBookingStatusData(bookingsByStatus);
        
        // Prepare trips by destination data
        const destinationCounts = {};
        trips.forEach(trip => {
          if (!destinationCounts[trip.destination]) {
            destinationCounts[trip.destination] = 0;
          }
          destinationCounts[trip.destination]++;
        });
        
        const tripsByDestination = Object.keys(destinationCounts).map(destination => ({
          name: destination,
          value: destinationCounts[destination]
        })).sort((a, b) => b.value - a.value).slice(0, 5); // Top 5 destinations
        
        setTripsByDestinationData(tripsByDestination);
        
        // Prepare recent activity
        const allActivities = [
          ...bookings.map(booking => ({
            type: 'booking',
            id: booking.id,
            date: booking.createdAt,
            description: `New booking #${booking.id.substring(0, 6)} created`
          })),
          ...trips.filter(trip => trip.status === 'completed').map(trip => ({
            type: 'trip',
            id: trip.id,
            date: trip.completedAt,
            description: `Trip #${trip.id.substring(0, 6)} completed`
          }))
        ];
        
        // Sort by date (newest first) and take the 10 most recent
        const sortedActivities = allActivities
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 10);
          
        setRecentActivity(sortedActivities);
        
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [timeRange]);
  
  // Helper function to prepare revenue data based on time range
  const prepareRevenueData = (bookings, timeRange) => {
    const now = new Date();
    let startDate;
    
    switch (timeRange) {
      case 'week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
    }
    
    // Filter bookings by date range
    const filteredBookings = bookings.filter(booking => {
      const bookingDate = new Date(booking.createdAt.seconds * 1000);
      return bookingDate >= startDate && bookingDate <= now;
    });
    
    // Group by date
    const revenueByDate = {};
    
    filteredBookings.forEach(booking => {
      if (booking.status !== 'cancelled' && booking.paymentStatus === 'completed') {
        const bookingDate = new Date(booking.createdAt.seconds * 1000);
        let dateKey;
        
        if (timeRange === 'week') {
          // Format as day of week (e.g., "Mon")
          dateKey = bookingDate.toLocaleDateString('en-US', { weekday: 'short' });
        } else if (timeRange === 'month') {
          // Format as day of month (e.g., "15")
          dateKey = bookingDate.getDate().toString();
        } else {
          // Format as month (e.g., "Jan")
          dateKey = bookingDate.toLocaleDateString('en-US', { month: 'short' });
        }
        
        if (!revenueByDate[dateKey]) {
          revenueByDate[dateKey] = 0;
        }
        
        revenueByDate[dateKey] += booking.amount || 0;
      }
    });
    
    // Convert to array format for chart
    return Object.keys(revenueByDate).map(date => ({
      date,
      revenue: revenueByDate[date]
    }));
  };
  
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Trips
              </Typography>
              <Typography variant="h4">
                {stats.totalTrips}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Bookings
              </Typography>
              <Typography variant="h4">
                {stats.totalBookings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h4">
                ${stats.totalRevenue.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Occupancy Rate
              </Typography>
              <Typography variant="h4">
                {stats.occupancyRate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Revenue Chart */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 360,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Revenue Over Time</Typography>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={timeRange}
                  onChange={handleTimeRangeChange}
                  label="Time Range"
                >
                  <MenuItem value="week">Last Week</MenuItem>
                  <MenuItem value="month">Last Month</MenuItem>
                  <MenuItem value="year">Last Year</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={revenueData}
                margin={{
                  top: 16,
                  right: 16,
                  bottom: 0,
                  left: 24,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#8884d8"
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Booking Status Chart */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 360,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Booking Status
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={bookingStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {bookingStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Top Destinations Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Top Destinations
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={tripsByDestinationData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" name="Trips" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
              overflow: 'auto'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Box>
              {recentActivity.map((activity, index) => (
                <Box key={index} mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    {new Date(activity.date.seconds * 1000).toLocaleString()}
                  </Typography>
                  <Typography variant="body1">
                    {activity.description}
                  </Typography>
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default MainDashboard;
